'use client';

import type { PrV2CommentResponse, PrV2DiscussionResponse } from '@/lib/pr-v2/types';
import type { Reactions } from '@/types/github';

import { TIMELINE_ITEM_TYPE } from '@/lib/pr-v2/timeline-types';

export const PR_V2_OPTIMISTIC_COMMENT_RETENTION_MS = 30_000;
export const PR_V2_OPTIMISTIC_REACTION_RETENTION_MS = 30_000;

type CommentTimelineItem = PrV2DiscussionResponse['timelineItems'][number];

function normalizeString(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : null;
}

function normalizeOptionalNumericString(value: number | string | null | undefined): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return typeof value === 'string' ? normalizeString(value) : null;
}

function normalizeLine(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function isOptimisticId(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().toLowerCase().startsWith('optimistic');
}

function getCommentRawNodeId(comment: PrV2CommentResponse): string | null {
  if (typeof comment.raw !== 'object' || !comment.raw || !('node_id' in comment.raw)) {
    return null;
  }

  const nodeId = (comment.raw as { node_id?: unknown }).node_id;
  return typeof nodeId === 'string' ? nodeId : null;
}

function getCommentRawIsOptimistic(comment: PrV2CommentResponse): boolean {
  if (typeof comment.raw !== 'object' || !comment.raw || !('is_optimistic' in comment.raw)) {
    return false;
  }

  return (comment.raw as { is_optimistic?: unknown }).is_optimistic === true;
}

function isOptimisticComment(comment: PrV2CommentResponse): boolean {
  return (
    getCommentRawIsOptimistic(comment) ||
    isOptimisticId(comment.id) ||
    isOptimisticId(comment.githubCommentId) ||
    isOptimisticId(getCommentRawNodeId(comment))
  );
}

function isCommentTimelineItem(item: CommentTimelineItem): boolean {
  return (
    item.type === TIMELINE_ITEM_TYPE.IssueComment || item.type === TIMELINE_ITEM_TYPE.ReviewComment
  );
}

function isOptimisticCommentTimelineItem(item: CommentTimelineItem): boolean {
  if (!isCommentTimelineItem(item)) {
    return false;
  }

  return isOptimisticId(item.id) || isOptimisticId(item.node_id);
}

function getCommentTimestampMs(comment: PrV2CommentResponse): number {
  const timestamp = comment.createdAtGithub ?? comment.updatedAtGithub;
  return timestamp ? Date.parse(timestamp) : Number.NaN;
}

function getTimelineTimestampMs(item: CommentTimelineItem): number {
  return item.timestamp ? Date.parse(item.timestamp) : Number.NaN;
}

function isFreshTimestamp(timestampMs: number, nowMs: number): boolean {
  return (
    Number.isFinite(timestampMs) && nowMs - timestampMs <= PR_V2_OPTIMISTIC_COMMENT_RETENTION_MS
  );
}

function areReactionBucketsEqual(existing: Reactions, fetched: Reactions): boolean {
  if (existing.length !== fetched.length) {
    return false;
  }

  const normalizeBucket = (bucket: Reactions) =>
    [...bucket]
      .map((reaction) => ({
        id: typeof reaction.id === 'number' ? reaction.id : null,
        content: typeof reaction.content === 'string' ? reaction.content : null,
        userId: typeof reaction.user?.id === 'number' ? reaction.user.id : null,
        login: typeof reaction.user?.login === 'string' ? reaction.user.login : null,
      }))
      .sort((left, right) => {
        const leftKey = `${left.id ?? ''}:${left.userId ?? ''}:${left.content ?? ''}:${left.login ?? ''}`;
        const rightKey = `${right.id ?? ''}:${right.userId ?? ''}:${right.content ?? ''}:${right.login ?? ''}`;
        return leftKey.localeCompare(rightKey);
      });

  const normalizedExisting = normalizeBucket(existing);
  const normalizedFetched = normalizeBucket(fetched);

  return normalizedExisting.every((reaction, index) => {
    const fetchedReaction = normalizedFetched[index];
    return (
      fetchedReaction &&
      reaction.id === fetchedReaction.id &&
      reaction.userId === fetchedReaction.userId &&
      reaction.content === fetchedReaction.content &&
      reaction.login === fetchedReaction.login
    );
  });
}

function reconcilePrV2ReactionBucketsWithOptimistic(
  existingDiscussion: PrV2DiscussionResponse | undefined,
  fetchedDiscussion: PrV2DiscussionResponse,
  nowMs: number,
): Pick<PrV2DiscussionResponse, 'reactions' | 'optimisticReactionBuckets'> {
  const optimisticBuckets = existingDiscussion?.optimisticReactionBuckets;
  if (!optimisticBuckets) {
    return {
      reactions: fetchedDiscussion.reactions,
      optimisticReactionBuckets: undefined,
    };
  }

  const nextReactions = { ...fetchedDiscussion.reactions };
  const nextOptimisticBuckets: NonNullable<PrV2DiscussionResponse['optimisticReactionBuckets']> =
    {};

  for (const [commentId, optimisticBucket] of Object.entries(optimisticBuckets)) {
    if (
      !Number.isFinite(optimisticBucket.updatedAtMs) ||
      nowMs - optimisticBucket.updatedAtMs > PR_V2_OPTIMISTIC_REACTION_RETENTION_MS
    ) {
      continue;
    }

    const numericCommentId = Number(commentId);
    if (!Number.isFinite(numericCommentId)) {
      continue;
    }

    const fetchedBucket =
      fetchedDiscussion.reactions[numericCommentId] ?? ([] as unknown as Reactions);
    if (areReactionBucketsEqual(optimisticBucket.reactions, fetchedBucket)) {
      continue;
    }

    nextReactions[numericCommentId] = optimisticBucket.reactions;
    nextOptimisticBuckets[numericCommentId] = optimisticBucket;
  }

  return {
    reactions: nextReactions,
    optimisticReactionBuckets:
      Object.keys(nextOptimisticBuckets).length > 0 ? nextOptimisticBuckets : undefined,
  };
}

function softStringFieldMatches(
  optimisticValue: string | null,
  canonicalValue: string | null,
): boolean {
  if (!optimisticValue || !canonicalValue) {
    return true;
  }

  return optimisticValue === canonicalValue;
}

function normalizeCommentMatchFields(comment: PrV2CommentResponse) {
  return {
    commentType: normalizeString(comment.commentType),
    body: normalizeString(comment.body),
    inReplyToId: normalizeOptionalNumericString(comment.inReplyToId),
    path: normalizeString(comment.path),
    line: normalizeLine(comment.line),
    userLogin: normalizeString(comment.userLogin),
  };
}

function normalizeTimelineMatchFields(item: CommentTimelineItem) {
  const inReplyToId =
    item.type === TIMELINE_ITEM_TYPE.ReviewComment
      ? normalizeOptionalNumericString(item.in_reply_to_id ?? item.parentId)
      : normalizeOptionalNumericString(item.parentId);

  return {
    commentType: normalizeString(item.type),
    body: normalizeString(item.content),
    inReplyToId,
    path: normalizeString('path' in item ? item.path : null),
    line: normalizeLine('line' in item ? item.line : null),
    userLogin: normalizeString(item.user?.login),
  };
}

function commentsSemanticallyMatch(
  optimisticComment: PrV2CommentResponse,
  canonicalComment: PrV2CommentResponse,
): boolean {
  const optimisticFields = normalizeCommentMatchFields(optimisticComment);
  const canonicalFields = normalizeCommentMatchFields(canonicalComment);

  return (
    optimisticFields.commentType === canonicalFields.commentType &&
    optimisticFields.body === canonicalFields.body &&
    optimisticFields.inReplyToId === canonicalFields.inReplyToId &&
    optimisticFields.path === canonicalFields.path &&
    optimisticFields.line === canonicalFields.line &&
    softStringFieldMatches(optimisticFields.userLogin, canonicalFields.userLogin)
  );
}

function timelineItemsSemanticallyMatch(
  optimisticItem: CommentTimelineItem,
  canonicalItem: CommentTimelineItem,
): boolean {
  const optimisticFields = normalizeTimelineMatchFields(optimisticItem);
  const canonicalFields = normalizeTimelineMatchFields(canonicalItem);

  return (
    optimisticFields.commentType === canonicalFields.commentType &&
    optimisticFields.body === canonicalFields.body &&
    optimisticFields.inReplyToId === canonicalFields.inReplyToId &&
    optimisticFields.path === canonicalFields.path &&
    optimisticFields.line === canonicalFields.line &&
    softStringFieldMatches(optimisticFields.userLogin, canonicalFields.userLogin)
  );
}

function collectFreshUnmatchedOptimisticComments(
  existingComments: PrV2CommentResponse[] | undefined,
  fetchedComments: PrV2CommentResponse[],
  nowMs: number,
): PrV2CommentResponse[] {
  if (!existingComments?.length) {
    return [];
  }

  const matchedFetchedIndexes = new Set<number>();
  const canonicalFetchedComments = fetchedComments.filter(
    (comment) => !isOptimisticComment(comment),
  );

  return existingComments.filter((comment) => {
    if (!isOptimisticComment(comment) || !isFreshTimestamp(getCommentTimestampMs(comment), nowMs)) {
      return false;
    }

    const matchedIndex = canonicalFetchedComments.findIndex(
      (candidate, index) =>
        !matchedFetchedIndexes.has(index) && commentsSemanticallyMatch(comment, candidate),
    );

    if (matchedIndex >= 0) {
      matchedFetchedIndexes.add(matchedIndex);
      return false;
    }

    return true;
  });
}

function collectFreshUnmatchedOptimisticTimelineItems(
  existingDiscussion: PrV2DiscussionResponse | undefined,
  fetchedTimelineItems: CommentTimelineItem[],
  nowMs: number,
): CommentTimelineItem[] {
  if (!existingDiscussion?.timelineItems.length) {
    return [];
  }

  const matchedFetchedIndexes = new Set<number>();
  const canonicalFetchedItems = fetchedTimelineItems.filter(
    (item) => !isOptimisticCommentTimelineItem(item),
  );

  return existingDiscussion.timelineItems.filter((item) => {
    if (
      !isOptimisticCommentTimelineItem(item) ||
      !isFreshTimestamp(getTimelineTimestampMs(item), nowMs)
    ) {
      return false;
    }

    const matchedIndex = canonicalFetchedItems.findIndex(
      (candidate, index) =>
        !matchedFetchedIndexes.has(index) && timelineItemsSemanticallyMatch(item, candidate),
    );

    if (matchedIndex >= 0) {
      matchedFetchedIndexes.add(matchedIndex);
      return false;
    }

    return true;
  });
}

export function reconcilePrV2CommentsWithOptimistic(
  existingComments: PrV2CommentResponse[] | undefined,
  fetchedComments: PrV2CommentResponse[],
  nowMs = Date.now(),
): PrV2CommentResponse[] {
  const freshOptimisticComments = collectFreshUnmatchedOptimisticComments(
    existingComments,
    fetchedComments,
    nowMs,
  );

  if (!freshOptimisticComments.length) {
    return fetchedComments;
  }

  return [...fetchedComments, ...freshOptimisticComments];
}

export function reconcilePrV2DiscussionWithOptimistic(
  existingDiscussion: PrV2DiscussionResponse | undefined,
  fetchedDiscussion: PrV2DiscussionResponse,
  nowMs = Date.now(),
): PrV2DiscussionResponse {
  const freshOptimisticTimelineItems = collectFreshUnmatchedOptimisticTimelineItems(
    existingDiscussion,
    fetchedDiscussion.timelineItems,
    nowMs,
  );

  const reconciledReactionState = reconcilePrV2ReactionBucketsWithOptimistic(
    existingDiscussion,
    fetchedDiscussion,
    nowMs,
  );

  if (!freshOptimisticTimelineItems.length) {
    return {
      ...fetchedDiscussion,
      reactions: reconciledReactionState.reactions,
      optimisticReactionBuckets: reconciledReactionState.optimisticReactionBuckets,
    };
  }

  return {
    ...fetchedDiscussion,
    reactions: reconciledReactionState.reactions,
    optimisticReactionBuckets: reconciledReactionState.optimisticReactionBuckets,
    timelineItems: [...fetchedDiscussion.timelineItems, ...freshOptimisticTimelineItems],
  };
}
